export default function GuidedMeditation() {
  const packs = [
    {
      title: 'Guided Breathing Pack',
      description: 'Short, practical sessions teaching simple yet powerful breathing techniques to reduce anxiety, release tension, and bring your nervous system back to calm — perfect for moments of stress, overwhelm, or daily resets.',
      icon: 'ri-lungs-line',
      color: 'from-[#E8F1F5] to-[#D1E3EA]'
    },
    {
      title: 'Guided Meditation Pack',
      description: 'Soothing meditations that guide you away from mental noise and into stillness, using gentle visualisations, grounding reflections, and relaxation techniques that support clarity and emotional balance.',
      icon: 'ri-mental-health-line',
      color: 'from-[#D1E3EA] to-[#B8D4E3]'
    },
    {
      title: 'Guided Progressive Relaxation Pack',
      description: 'A structured, full-body relaxation experience guiding you through slow muscle release to melt away physical tension. Especially helpful for stress, sleep preparation, or high-tension days — and particularly effective if you find traditional meditation difficult or have ADHD-related traits.',
      icon: 'ri-body-scan-line',
      color: 'from-[#E8F1F5] to-[#C8DCE8]'
    }
  ];

  return (
    <section id="meditation" className="py-24 bg-gradient-to-b from-[#E8F1F5] to-[#F0F4F8]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-5xl font-serif text-[#2F7FB7] mb-6">Guided Meditation Series</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            This carefully crafted collection of guided practices helps you slow down, settle your thoughts, and reconnect with inner balance.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {packs.map((pack, index) => (
            <div 
              key={index}
              className={`bg-gradient-to-br ${pack.color} backdrop-blur-sm rounded-3xl p-8 hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 cursor-pointer group border border-[#D1E3EA]`}
            >
              <div className="flex items-center justify-center w-20 h-20 rounded-2xl bg-white/60 mb-6 group-hover:scale-110 transition-transform">
                <i className={`${pack.icon} text-4xl text-[#2F7FB7]`}></i>
              </div>
              <h3 className="text-2xl font-serif text-[#2F7FB7] mb-4">{pack.title}</h3>
              <p className="text-gray-700 leading-relaxed mb-6">{pack.description}</p>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 flex items-center gap-2">
                  <i className="ri-play-circle-line"></i>
                  {pack.sessions} sessions
                </span>
                <a href="/download" className="text-[#2F7FB7] font-medium inline-flex items-center gap-2 group-hover:gap-3 transition-all whitespace-nowrap cursor-pointer">
                  Explore
                  <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-3xl p-10 shadow-lg">
          <div className="flex items-start gap-6">
            <div className="flex items-center justify-center w-16 h-16 rounded-2xl bg-[#E8F1F5] flex-shrink-0">
              <i className="ri-heart-pulse-line text-3xl text-[#2F7FB7]"></i>
            </div>
            <div>
              <h3 className="text-2xl font-serif text-[#2F7FB7] mb-3">Why Guided Practices?</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                These guided sessions are designed to be accessible for everyone — whether you're new to meditation or have an established practice. Each technique is grounded in evidence-based approaches to help you cultivate calm, clarity, and emotional balance.
              </p>
              <p className="text-gray-600 leading-relaxed">
                From quick breathing resets to deep progressive relaxation, you'll find the right practice for whatever moment you're in — stress, overwhelm, sleep preparation, or simply taking time for yourself.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
